import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerOverlay,
  Radio
} from '@chakra-ui/react';
import { t } from 'i18next';
import { useState } from 'react';
import AddReport from '../api/mutations/AddReport';
import { CheckRadioFill, CheckRadioLine } from '@peacecover/icon';

const REPORT_KEYS = ['date', 'image', 'copyright', 'other'];
const CATEGORY_BY_REPORT_KEY = {
  'date': 'incorrect',
  'image': 'incorrect',
  'copyright': 'incorrect',
  'other': 'other'
};


interface IReportLocationEventBottomDrawerProps {
  isOpen: boolean;
  eventId: string;
  onClose: () => void;
  onReport: () => void;
}

export default function ReportLocationEventBottomDrawer({
  eventId,
  isOpen,
  onReport,
  onClose
}: IReportLocationEventBottomDrawerProps) {
  const [selectedReportKey, setSelectedReportKey] =
    useState<keyof typeof CATEGORY_BY_REPORT_KEY>('date');
  const [etcContent, setEtcContent] = useState<string>('');

  const reportEvent = async () => {
    const response = await AddReport({
      reportableType: 'Event',
      reportableId: eventId,
      category: CATEGORY_BY_REPORT_KEY[selectedReportKey],
      content:
        selectedReportKey === 'other'
          ? etcContent
          : t(`ReportLocationEventBottomDrawer.${selectedReportKey}`),
      dataUrl: null
    });

    if (response.ok) {
      onReport();
      onClose();
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="bottom">
      <DrawerOverlay />
      <DrawerContent
        motionProps={{
          variants: {
            enter: {
              y: '0%',
              transition: { duration: 0.15 }
            },
            exit: {
              y: '100%',
              transition: { duration: 0.1 }
            }
          }
        }}
        style={{
          maxWidth: 'calc(var(--chakra-sizes-xl))',
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          left: '',
          right: ''
        }}
      >
        <DrawerBody padding={0}>
          <div className="text-center pt-[27px] pb-[11px] font-semibold text-[17px] text-main-light-1 dark:text-main-dark-1">
            {t('ReportLocationEventBottomDrawer.title')}
          </div>
          {REPORT_KEYS.map((key) => {
            return (
              <div
                key={key}
                className="flex flex-row px-[20px] py-[13px] gap-[8px] content-center"
                onClick={() => setSelectedReportKey(key as keyof typeof CATEGORY_BY_REPORT_KEY)}
              >
                <button type="button">
                  {selectedReportKey === (key as keyof typeof CATEGORY_BY_REPORT_KEY) ? (
                    <CheckRadioFill
                      size={24}
                      className="text-primary-light dark:text-primary-dark"
                    />
                  ) : (
                    <CheckRadioLine size={24} className="text-text-sub-lv2" />
                  )}
                </button>
                <div className="font-normal text-[15px] text-main-light-2 dark:text-main-light-2">
                  {t(`ReportLocationEventBottomDrawer.${key}`)}
                </div>{' '}
              </div>
            );
          })}
          {selectedReportKey === 'other' && (
            <div className="flex flex-col mx-[48px]">
              <input
                type="text"
                className="h-[80px] min-h-[80px] rounded-[12px] p-[20px]
                  bg-secondary-light dark:bg-secondary-dark
                  text-[15px] font-medium text-main-light-2 dark:text-main-dark-2
                  focus:outline-none focus:ring-0
                  placeholder:text-sub-1 placeholder:font-medium placeholder:text-[15px] placeholder:leading-[100%]
                  "
                placeholder={t('ReportLocationEventBottomDrawer.placeholder').toString()}
                value={etcContent}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 50) {
                    setEtcContent(value);
                  }
                }}
              />
              <div className="text-right text-[13px] font-medium text-main-light-4 dark:text-main-dark-4 mt-[4px]">
                {etcContent?.length || 0}/50
              </div>
            </div>
          )}
        </DrawerBody>
        <DrawerFooter
          padding={0}
          className="w-full mt-[20px] !safeAreaMarginBottom !bg-primary-light dark:!bg-primary-dark"
        >
          <div
            className="w-full py-[14px] bg-primary-light dark:bg-primary-dark text-white text-center font-semibold text-[17px] pb-[calc(env(safe-area-inset-bottom)+14px)]"
            onClick={() => reportEvent()}
          >
            {t('ReportLocationEventBottomDrawer.confirm')}
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
